import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import GenericModal from '../generic_modal';
import Warning from '../../../components/warning';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../forecast-app/shared/components/modals/types';

/**
 * One instance that stops the delete. `sequence` is its position among the set's
 * current tasks (gaps from deleted tasks closed), which is what the refusal
 * names -- task ids are only creation order.
 */
export interface BlockedTaskSetInstance {
	readonly companyTaskId?: number | null;
	readonly sequence?: number | null;
	readonly reason?: string | null;
}

export interface DeleteTaskSetModalPayload {
	/** The set's name, shown in the confirmation so the reader can check it. */
	setName: string;
	/** Every instance the set has, not the number that could go. */
	instanceCount: number;
	blockedInstances: ReadonlyArray<BlockedTaskSetInstance>;
	/** Runs only from the proceeding state; the refusal has nothing to confirm. */
	onDelete: () => void;
	/**
	 * Opens the set's edit form on its Ends field -- the thing a reader who
	 * cannot delete a set usually wants. Absent where there is nowhere to send
	 * them, and the refusal then states the alternative in prose without offering
	 * a button that goes nowhere.
	 */
	onEndEarly?: () => void;
}

export const showDeleteTaskSetModal = (props: DeleteTaskSetModalPayload): void => {
	showModal({
		type: MODAL_TYPE.DELETE_TASK_SET,
		...props,
	});
};

const BlockedHeading = styled.div`
	margin-top: 18px;
	font-size: 12px;
	font-weight: 600;
	color: #7d7d7d;
`;

const BlockedList = styled.ul`
	list-style: none;
	margin: 8px 0 0;
	padding: 0;
	max-height: 210px;
	overflow-y: auto;
`;

const BlockedRow = styled.li`
	display: flex;
	align-items: baseline;
	gap: 8px;
	padding: 7px 0;
	border-bottom: 1px solid #f3f3f3;
	font-size: 13px;
`;

/** Monospaced so a column of ids lines up rather than ragging on digit width. */
const WorkId = styled.span`
	font-variant-numeric: tabular-nums;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.87);
`;

const RowLabel = styled.span`
	color: rgba(0, 0, 0, 0.87);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

/** The reason, set apart so the row reads id, what, then why. */
const RowReason = styled.span`
	margin-left: auto;
	padding-left: 12px;
	font-style: italic;
	color: #7d7d7d;
	white-space: nowrap;
`;

const Note = styled.div`
	margin-top: 16px;
	font-size: 13px;
	line-height: 19px;
	color: #7d7d7d;
`;

/**
 * Each blocking reason in the reader's words.
 *
 * Every one of these is recomputed from current data and can clear, so none of
 * them is phrased as a permanent property of the task. An unrecognised reason
 * falls back to a truthful vague line rather than rendering the raw enum.
 */
const REASON_KEYS: {[reason: string]: string} = {
	HAS_INVOICED_TIME: 'task_set.delete.reason_invoiced',
	HAS_LOCKED_TIME: 'task_set.delete.reason_locked',
	HAS_TIME_REGS: 'task_set.delete.reason_time_entries',
	HAS_OTHER_PEOPLES_TIME_REGS: 'task_set.delete.reason_other_peoples_time',
	JIRA: 'task_set.delete.reason_jira',
	GITHUB: 'task_set.delete.reason_github',
	UNIT4: 'task_set.delete.reason_unit4',
	BLOCKED_BY_SUBTASK: 'task_set.delete.reason_subtask',
	NO_PERMISSION: 'task_set.delete.reason_permission',
};

/**
 * The delete confirmation, and the refusal when something blocks it.
 *
 * One component for both, because which one shows is a single boolean: the set
 * is deletable or it is not. Splitting them would let the two drift apart on the
 * set name, the instance count, and the chrome.
 *
 * It uses the product's own modal chrome rather than the design system's,
 * deliberately: the design system has no destructive button emphasis, so a
 * delete confirmed in the brand blue would be the only non-red delete in the
 * product.
 *
 * Deleting a set removes every instance or removes none, so the refusal never
 * offers to delete the rest. A partial delete would leave a set whose survivors
 * no longer match its rule, and the reader asked for the set to go.
 */
export const DeleteTaskSetModal = ({
	closeModal,
	setName,
	instanceCount,
	blockedInstances,
	onDelete,
	onEndEarly,
}: ModalProps<DeleteTaskSetModalPayload>): JSX.Element => {
	const {formatMessage} = useIntl();
	const blocked = blockedInstances || [];
	const isBlocked = blocked.length > 0;

	const handleDelete = () => {
		onDelete();
		closeModal();
	};

	if (!isBlocked) {
		const content = (
			<div>
				<Warning message={formatMessage({id: 'task_set.delete.confirm'}, {name: setName, count: instanceCount})} />
				{/* Count-aware: a set that has generated nothing has no comments,
				    attachments or estimates to warn about. */}
				<div className="warning-part-2">
					{formatMessage({id: 'task_set.delete.consequences'}, {count: instanceCount})}
				</div>
			</div>
		);

		return (
			<GenericModal
				top={true}
				className="default-warning-modal"
				cy="delete-task-set-modal"
				headerText={formatMessage({id: 'task_set.delete.title'})}
				closeModal={closeModal}
				content={content}
				buttons={[
					{
						text: formatMessage({id: 'common.cancel'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.WHITE,
						cy: 'delete-task-set-cancel',
					},
					{
						// The count rides on the button, not a bare "Delete". It is the
						// last thing read before the click, and it is the number of
						// tasks that disappear.
						text: formatMessage({id: 'task_set.delete.confirm_button'}, {count: instanceCount}),
						callback: handleDelete,
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.RED,
						cy: 'delete-task-set-confirm',
					},
				]}
			/>
		);
	}

	const blockedContent = (
		<div>
			<Warning
				message={formatMessage(
					{id: 'task_set.delete.blocked_summary'},
					{blocked: blocked.length, total: instanceCount}
				)}
			/>
			<BlockedHeading>{formatMessage({id: 'task_set.delete.blocked_heading'})}</BlockedHeading>
			<BlockedList data-cy="delete-task-set-blocked-list">
				{blocked.map((instance, index) => (
					<BlockedRow key={instance.companyTaskId ?? index}>
						<WorkId>{`T${instance.companyTaskId ?? ''}`}</WorkId>
						<RowLabel>
							{formatMessage(
								{id: 'task_set.delete.blocked_row'},
								{name: setName, sequence: instance.sequence ?? '', total: instanceCount}
							)}
						</RowLabel>
						<RowReason>
							{formatMessage({
								id: REASON_KEYS[instance.reason || ''] || 'task_set.delete.reason_unknown',
							})}
						</RowReason>
					</BlockedRow>
				))}
			</BlockedList>
			{/* Blocks clear. Saying otherwise sends the reader away believing a set
			    is permanently undeletable when unlocking the period would fix it. */}
			<Note>{formatMessage({id: 'task_set.delete.blocked_temporary'})}</Note>
			<Note>{formatMessage({id: 'task_set.delete.blocked_alternative'})}</Note>
		</div>
	);

	/* Cancel, plus the way out the refusal has just pointed at. The canvas also
	   draws "Show me", which still has no target -- there is no scroll-to-instance
	   affordance, and a button that goes nowhere is worse than no button. The list
	   above already names every blocked instance by its work id.

	   "End the set early" is offered only when the caller can actually open the
	   Ends field. Without that it would be a second dead button beside the first,
	   and the prose above already says what to do. */
	const blockedButtons = [
		{
			text: formatMessage({id: 'common.cancel'}),
			style: BUTTON_STYLE.FILLED,
			color: BUTTON_COLOR.WHITE,
			cy: 'delete-task-set-blocked-cancel',
		},
	];
	if (onEndEarly) {
		blockedButtons.push({
			text: formatMessage({id: 'task_set.delete.end_early'}),
			callback: () => onEndEarly(),
			style: BUTTON_STYLE.FILLED,
			color: BUTTON_COLOR.GREEN,
			cy: 'delete-task-set-end-early',
		} as never);
	}

	return (
		<GenericModal
			top={true}
			className="default-warning-modal"
			cy="delete-task-set-blocked-modal"
			headerText={formatMessage({id: 'task_set.delete.blocked_title'})}
			closeModal={closeModal}
			content={blockedContent}
			buttons={blockedButtons}
		/>
	);
};

export default DeleteTaskSetModal;
